import { Alert } from '@piggytechng/component-library';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { sessionStorageEnum } from './enums';
import { v4 as uuidv4 } from 'uuid';

enum StatusCode {
  Unauthorized = 401,
  Forbidden = 403,
  TooManyRequests = 429,
  InternalServerError = 500,
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, days = 365) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/`;
}

function getUniqueId() {
  const COOKIE_KEY = 'device_uid';
  let uniqueId = getCookie(COOKIE_KEY) || localStorage.getItem('uniqueId');
  if (!uniqueId) {
    uniqueId = uuidv4();
  }

  setCookie(COOKIE_KEY, uniqueId);
  // Mirror to localStorage so other code that reads it there still works
  try {
    localStorage.setItem('uniqueId', uniqueId);
  } catch (_) {
    /* ignore */
  }
  return uniqueId.toUpperCase();
}

export const uniqueDeviceId = getUniqueId();

const device = {
  id: 'desktop',
  model: 'web',
  brand: 'piggyvest',
  name: 'admin-dashboard',
  type: 'admin-device',
  uniqueId: uniqueDeviceId,
  systemName: 'piggyvest',
  powerState: 'active',
  manufacturer: 'generic',
};

const headers = {
  'Content-Type': 'application/json',
  'X-Authorization': process.env.REACT_APP_X_AUTHORIZATION,
  theme: 'light',
  Platform: 'ios',
  version: '5.0.0',
  device: JSON.stringify(device),
  Accept: 'application/json',
  'Api-Reference-Id': 59,
  hdp: 8.666666666666666,
  wdp: 4,
  'device-width': '400',
  'device-height': '400',
  'Access-Control-Allow-Origin': '*',
};

// Extend AxiosRequestConfig with a per-request `hideError` flag.
// When true, all error toasts for that request are suppressed.
declare module 'axios' {
  export interface AxiosRequestConfig {
    hideError?: boolean;
  }
}

const API_ORIGIN = (() => {
  try {
    return new URL(process.env.REACT_APP_BASE_URL ?? '').origin;
  } catch {
    return '';
  }
})();

const isForeignHost = (url?: string) => {
  if (!url || !/^https?:\/\//i.test(url)) return false;
  try {
    return new URL(url).origin !== API_ORIGIN;
  } catch {
    return true;
  }
};

const injectToken = (config: AxiosRequestConfig): AxiosRequestConfig | any => {
  try {
    const key = sessionStorageEnum['session.storage.token'];
    const token = sessionStorage.getItem(key);

    if (token != null && !isForeignHost(config?.url)) {
      if (!config) {
        config = {};
      }
      if (!config.headers) {
        config.headers = {};
      }
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  } catch (error) {
    const err: any = error;
    throw new Error(err);
  }
};

const UNAUTHORIZED_BURST_LIMIT = 3;
const UNAUTHORIZED_BURST_WINDOW = 60000;

class Http {
  private instance: AxiosInstance | null = null;
  private lastAlertTime = 0;
  private lastUnauthorizedAlertTime = 0;
  private unauthorizedHits: number[] = [];
  private isLoggingOut = false;
  private isOnline: boolean | null = null;

  private get http(): AxiosInstance {
    return this.instance != null ? this.instance : this.initHttp();
  }

  private throttleAlert(
    message: string,
    alertType: 'error' | 'info' | 'success' = 'error',
    interval: number = 5000,
  ): void {
    const now = Date.now();
    if (now - this.lastAlertTime >= interval) {
      switch (alertType) {
        case 'info':
          Alert.info(message);
          break;
        case 'success':
          Alert.success(message);
          break;
        default:
          Alert.error(message);
          break;
      }
      this.lastAlertTime = now;
    }
  }

  private forceLogout(): void {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;

    localStorage.clear();
    sessionStorage.clear();
    setTimeout(() => {
      window.location.href = '/login';
    }, 1200);
  }

  initHttp() {
    const http = axios.create({
      baseURL: process.env.REACT_APP_BASE_URL,
      headers,
    });

    http.interceptors.request.use(injectToken, (error) => Promise.reject(error));

    http.interceptors.response.use(
      (response) => {
        this.unauthorizedHits = [];
        return response;
      },
      (error) => {
        const { response, config: requestConfig } = error;
        const hideError = requestConfig?.hideError ?? false;

        if (!hideError) {
          if (response?.data?.message && response.status !== StatusCode.Unauthorized) {
            this.throttleAlert(response.data.message);
          } else if (response?.status !== StatusCode.Unauthorized) {
            this.throttleAlert(
              'Something went wrong. Please check your connection and try again or contact support...',
            );
          }
        }

        return this.handleError(response, hideError);
      },
    );

    this.instance = http;
    return http;
  }

  request<T = any, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R> {
    return this.http.request(config);
  }

  get<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.http.get<T, R>(url, config);
  }

  post<T = any, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> {
    return this.http.post<T, R>(url, data, config);
  }

  put<T = any, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> {
    return this.http.put<T, R>(url, data, config);
  }

  patch<T = any, R = AxiosResponse<T>>(url: string, data?: T, config?: AxiosRequestConfig): Promise<R> {
    return this.http.patch<T, R>(url, data, config);
  }

  delete<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.http.delete<T, R>(url, config);
  }

  // Handle errors based on status codes.
  // `hideError` suppresses all toasts; the 401 redirect fires regardless
  // since it is a security action, not just a UX notification.
  private handleError(error: { status: any }, hideError = false) {
    const { status } = error;

    const isLoggedIn = localStorage.getItem('isLoggedIn');

    switch (status) {
      case StatusCode.InternalServerError: {
        if (!hideError) this.throttleAlert('Internal server error. Please try again later.');
        break;
      }
      case StatusCode.Forbidden: {
        if (!hideError) this.throttleAlert('You do not have permission to perform this action.');
        break;
      }
      case StatusCode.Unauthorized: {
        if (this.isLoggingOut || window.location.pathname === '/login') break;

        if (!hideError) this.throttleAlert('Unauthorized access, please login again');

        const currentTime = Date.now();

        this.unauthorizedHits = this.unauthorizedHits.filter(
          (hitTime) => currentTime - hitTime < UNAUTHORIZED_BURST_WINDOW,
        );
        this.unauthorizedHits.push(currentTime);

        if (this.unauthorizedHits.length >= UNAUTHORIZED_BURST_LIMIT) {
          this.unauthorizedHits = [];
          this.lastUnauthorizedAlertTime = currentTime;
          Alert.error('Too many unauthorized calls, login again');
          this.forceLogout();
          break;
        }

        if (currentTime - this.lastUnauthorizedAlertTime >= 10000 && this.isOnline) {
          isLoggedIn && Alert.info('Your session has expired. Please, login again');
          this.forceLogout();

          this.lastUnauthorizedAlertTime = currentTime;
        }

        break;
      }
      case StatusCode.TooManyRequests: {
        if (!hideError) this.throttleAlert('Too many requests. Please slow down and try again later.');
        break;
      }
      default:
        break;
    }

    return Promise.reject(error);
  }
}

export const httpClient = new Http();
